import { View } from 'react-native';
import React, { useMemo } from 'react';

import { OrderMessageDataBase } from '@lib/types';
import { ReceiptRow } from '@components/ReceiptRow';
import { makeStyles } from '@providers/Theme';
import { useTranslation } from '@providers/I18n';

import {
  calculatePromoValue,
  calculateTaxes,
  calculateTotal,
} from './utils/receipt';

export type ReceiptProps = {
  order: OrderMessageDataBase<false>;
};

const useStyles = makeStyles((theme) => ({
  field: {
    ...theme.mixins.padding(2, 0),
    borderTopColor: theme.palette.grey[300],
    borderTopWidth: 1,
    backgroundColor: theme.palette.base[100],
  },
}));

export const Receipt: React.FC<ReceiptProps> = ({ order }) => {
  const { 
    receipt = {} as any, 
    userPromo, 
    orderLevelSettlement, 
    paymentType = 'CASH' 
  } = order || {};
  
  const styles = useStyles();
  const { t } = useTranslation();

  const taxes = useMemo(() => {
    return calculateTaxes(receipt) || 0;
  }, [receipt]);

  const promoDiscount = useMemo(() => {
    if (userPromo) {
      return calculatePromoValue(receipt, userPromo) || 0;
    }
    return 0;
  }, [receipt, userPromo]);

  const total = useMemo(() => {
    return calculateTotal(receipt) || 0;
  }, [receipt]);

  const TotalWithoutTips = useMemo(() => {
    const currentTotal = receipt?.total || 0;
    const currentTip = receipt?.driverTip || 0;
    
    if (currentTotal > 0 && currentTip > 0) {
      return currentTotal - currentTip;
    }
    return currentTotal;
  }, [receipt?.total, receipt?.driverTip]);

  return (
    <View style={styles.field} testID="receipt">
      <ReceiptRow
        accessibilityLabel="item"
        testID="subTotal"
        name={t('orderDetails.receipt.subTotal', 'Sub Total')}
        // Fixed: Use ?? so that a 0 value is treated as valid
        value={
          ((receipt.subTotalAfterDiscount ?? receipt.subTotal) || 0).toFixed(2) +
          t('common.egp', 'EGP')
        }
      />
      
      <ReceiptRow
        accessibilityLabel="item"
        testID="deliveryFees"
        name={t('orderDetails.receipt.deliveryFees', 'Delivery Fees')}
        value={(receipt.deliveryFee || 0).toFixed(2) + t('common.egp', 'EGP')}
      />

      <ReceiptRow
        accessibilityLabel="item"
        testID="tax"
        name={t('orderDetails.receipt.tax', 'Tax')}
        value={`(${receipt.tax || 0}%) ${(taxes || 0).toFixed(2)} ${t('common.egp', 'EGP')}`}
      />

      <ReceiptRow
        accessibilityLabel="item"
        testID="wallet"
        name={t('orderDetails.receipt.walletDiscount', 'Paid with elmenus wallet')}
        value={
          (receipt.paidWithWallet && (receipt.walletDiscount > 0)
            ? receipt.walletDiscount
            : 0
          ).toFixed(2) + t('common.egp', 'EGP')
        }
      />

      <ReceiptRow
        accessibilityLabel="item"
        testID="paymentMethod"
        name={t('orderDetails.receipt.paymentMethod', 'Payment Method')}
        value={orderLevelSettlement ? t('orderDetails.receipt.runnerPay', 'Runner will Pay') : t('orderDetails.receipt.elmenusSettlement', 'elmenus Settlement')}
      />

      {userPromo && (
        <ReceiptRow
          accessibilityLabel="item"
          testID="promoDiscount"
          name={t('orderDetails.receipt.promoDiscount', 'Promo discount')}
          value={(promoDiscount || 0).toFixed(2) + t('common.egp', 'EGP')}
        />
      )}

      <ReceiptRow
        accessibilityLabel="item"
        testID="paymentType"
        name={t('orderDetails.receipt.paymentType', 'Payment Type')}
        value={t('orderDetails.receipt.payment', {
          name: t(paymentType),
        })}
        image={
          paymentType === 'CREDIT_CARD'
            ? require('./assets/creditcard.png')
            : require('./assets/cash.png')
        }
        imgHeight={paymentType === 'CREDIT_CARD' ? 17 : 24}
      />

      <ReceiptRow
        accessibilityLabel="item"
        testID="total"
        name={t('orderDetails.receipt.total', 'Total')}
        value={
          (orderLevelSettlement ? total : TotalWithoutTips).toFixed(2) +
          t('common.egp', 'EGP')
        }
        TypographyProps={{
          weight: 'extraBold',
          style: { color: 'black' },
        }}
      />
    </View>
  );
};
